// store/usePlanningStore.ts
import { computed, reactive, shallowRef, toRefs, ref, triggerRef } from 'vue';
import { defineStore } from 'pinia';
import { useManualBalanceStore } from '@/rennovation/planning-view/store/useManualBalanceStore';
import { ApiEvaluateSingleDriverResponse } from '@/rennovation/planning-view/types/api/roster-response';
import { ApiManualBalance } from '@/rennovation/planning-view/types/api/manual-balance-response';
import { DateTime } from 'luxon';
import { useAgreementApi } from '@/rennovation/common/services/api/useAgreementApi';
import { DayUiItem, useDayStore } from './useDayStore';
import { useDutyStore } from './useDutyStore';
import { useUserStore } from './useUserStore';
import { useDutyApi } from '@/rennovation/common/services/api/useDutyApi';
import { useUserBridgeStore } from './userBridge';
import { useRosterStore } from './useRosterStore';
import type { ApiDuty } from '@/rennovation/planning-view/types/api/duties-response';
import type { ApiDriverUser } from '@/rennovation/planning-view/types/api/users-response';
import type { AgreementPayload, DutyChangeLogPayload } from '@/rennovation/planning-view/types/payloads/duty-payloads';
import { PlanningLoadingState, PlanningUIState, type IndicatorOption } from '@/rennovation/planning-view/types/state/planning-view-state';
import { STORAGE_KEYS } from '../constants';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { DutyItemResponse } from '../types/models/duty';

interface PlanningViewPayload {
	firstDate: string;
	numberOfDays?: number;
}

export const usePlanningStore = defineStore('planning', () => {
	const usersById = shallowRef<Record<string, ApiDriverUser>>({});
	const dutiesById = shallowRef<Record<string, ApiDuty>>({});
	const evaluationsById = shallowRef<Record<string, ApiEvaluateSingleDriverResponse>>({});
	const drivers = shallowRef<Record<string, ApiDriverUser>>({});
	const allDriverIds = shallowRef<string[]>([]);

	// ── STATISTICS REFS POOL ──
	const statistics = shallowRef<Record<string, any>>({});
	const singleStatistics = shallowRef<any>({});
	const statisticsByStatus = shallowRef<Record<string, Record<string, Record<string, any>>>>({});
	const extraPayCurrency = shallowRef<string>('CHF');

	const ui = reactive<Omit<PlanningUIState, 'drivers'>>({
		filters: {
			userList: null,
			selectedUserGroups: [],
			searchQuery: '',
			showInactiveUsers: true,
			userStatus: 'all',
			selectedIndicators: [],
			showRightPanel: false,
			fromDate: null,
			toDate: null,
			firstDate: null,
			violations: null,
			filteredDrivers: null,
			selectedStats: null,
			combinedFilteredDrivers: null,
		},
		calendar: {
			startDate: '',
			endDate: '',
			viewMode: 'day',
		},
		selectedDuty: null,
		selectedDate: null,
		selectedDriver: null,
		selectedUserId: null,
		isSidebarOpen: false,
	});

	const userStore = useUserStore();
	const dutyStore = useDutyStore();
	const dayStore = useDayStore();
	const alertStore = useAlertStore();
	const dutyApi = useDutyApi();
	const agreementApi = useAgreementApi();

	const isStreamingPaused = ref(false);
	const { checkPermisions } = useUserBridgeStore();

	const loading = reactive<PlanningLoadingState & { statisticsLoading: boolean }>({
		initial: false,
		assignments: false,
		savingIds: new Set<string>(),
		manualBalances: false,
		statisticsLoading: false,
	});

	const errors = reactive<{
		fetch: string | null;
		save: string | null;
		evaluation: string | null;
		manualBalances: string | null;
		statisticsError: string | null;
	}>({
		fetch: null,
		save: null,
		evaluation: null,
		manualBalances: null,
		statisticsError: null,
	});

	const driversArray = computed<ApiDriverUser[]>(() => Object.values(drivers.value));

	const isUserActive = (user: ApiDriverUser): boolean => {
		if (user.deleted) return false;

		if (!user.dateOfLeaving) return true;

		const leavingDate = DateTime.fromISO(user.dateOfLeaving).endOf('day');
		return leavingDate >= DateTime.now();
	};

	const sortByName = (a: ApiDriverUser, b: ApiDriverUser) => (a.sortName ?? '').localeCompare(b.sortName ?? '', undefined, { sensitivity: 'base' });

	const allUsers = computed<ApiDriverUser[]>(() => Object.values(drivers.value).sort(sortByName));

	const activeUsers = computed<ApiDriverUser[]>(() => allUsers.value.filter((user) => isUserActive(user)));

	const inactiveUsers = computed<ApiDriverUser[]>(() => allUsers.value.filter((user) => !isUserActive(user)));

	const manualWorktimeBalances = computed(() => Object.values(manualBalancesById.value));

	const getEvaluationByDriverId = (driverId: string): ApiEvaluateSingleDriverResponse | undefined => evaluationsById.value[driverId];
	const manualBalancesById = shallowRef<Record<string, ApiManualBalance>>({});

	function canEditDutyForDate(isoDate: string) {
		const dayStatus = dayStore.days[isoDate]?.data?.status;
		if (dayStatus === 'locked') return false;

		const canEditDuties = checkPermisions('duties.edit');
		const canEditActive = checkPermisions('roster.edit.active');
		const canEditInactive = checkPermisions('roster.edit.inactive');

		if (!canEditDuties) return false;
		if (canEditDuties && canEditActive && canEditInactive) return true;
		if (!dayStatus) {
			console.warn('Could not find any day status.');
			return false;
		}

		const belongsToActiveRoster = ['past', 'published'].includes(dayStatus);
		return belongsToActiveRoster ? canEditActive : canEditInactive;
	}

	function setStats(response: any) {
		if (!statistics.value || typeof statistics.value !== 'object') {
			statistics.value = {};
		}

		const batchUpdates = { ...statistics.value };

		let statsArray: any[] = [];
		if (Array.isArray(response)) {
			statsArray = response;
		} else if (response && Array.isArray(response.data)) {
			statsArray = response.data;
		} else if (response && response.data && Array.isArray(response.data.data)) {
			statsArray = response.data.data;
		}

		statsArray.forEach((s: any) => {
			if (!s || !s.user) return;

			const cleanKey = s.user.split('_')[0];

			batchUpdates[cleanKey] = s;
			batchUpdates[`${cleanKey}_driver`] = s;
			batchUpdates[s.user] = s;
		});

		statistics.value = batchUpdates;
		triggerRef(statistics);
		return statistics.value;
	}

	async function getStatistics(date: string, users: string[]): Promise<Record<string, any>> {
		loading.statisticsLoading = true;
		errors.statisticsError = null;
		try {
			const response = await dutyApi.loadStatistics(date, users);
			setStats(response);
		} catch (error) {
			errors.statisticsError = error instanceof Error ? error.message : 'Error loading statistics';
			return statistics.value || {};
		} finally {
			loading.statisticsLoading = false;
		}
	}

	async function loadSingleStatistics(date: string, user: string): Promise<any> {
		errors.statisticsError = null;
		try {
			const response = await dutyApi.loadStatistics(date, [user]);
			if (response?.length) {
				singleStatistics.value = response[0];
			}
			triggerRef(singleStatistics);
			return singleStatistics.value;
		} catch (error) {
			errors.statisticsError = error instanceof Error ? error.message : 'Error loading single statistics';
		}
	}

	async function loadStatisticsByDayStatus(dayStatus: string): Promise<void> {
		errors.statisticsError = null;
		try {
			const response = await dutyApi.loadStatisticsByDayStatus(dayStatus);
			const statusMap: Record<string, Record<string, any>> = {};

			response.data.forEach((statistic: any) => {
				if (!statusMap[statistic.day]) {
					statusMap[statistic.day] = {};
				}
				statusMap[statistic.day][statistic.user] = statistic;
			});

			statisticsByStatus.value = {
				...statisticsByStatus.value,
				[dayStatus]: statusMap,
			};
		} catch (error) {
			errors.statisticsError = error instanceof Error ? error.message : 'Error loading category statistics';
		}
	}

	function clearStatistics(): void {
		statistics.value = {};
		singleStatistics.value = {};
		statisticsByStatus.value = {};
	}

	async function load({ firstDate, numberOfDays = 80 }: PlanningViewPayload): Promise<Record<string, ApiDriverUser> | undefined> {
		return await initPlanningView({ firstDate, numberOfDays });
	}

	async function initPlanningView(params?: PlanningViewPayload): Promise<Record<string, ApiDriverUser> | undefined> {
		errors.fetch = null;
		try {
			const daysVal = dayStore.days;
			const hasDays = Array.isArray(daysVal) && daysVal.length > 0;

			let from = ui.filters.fromDate ? DateTime.fromISO(ui.filters.fromDate) : undefined;
			let to = ui.filters.toDate ? DateTime.fromISO(ui.filters.toDate) : undefined;

			if (hasDays && to === undefined) {
				const lastDay = daysVal[daysVal.length - 1];
				if (lastDay) to = DateTime.fromISO(lastDay.date);
			}
			if (hasDays && from === undefined) {
				const firstDay = daysVal[0];
				if (firstDay) from = DateTime.fromISO(firstDay.date);
			}
			if (!from || !to) return;

			await Promise.all([dayStore.loadDateRangeForDayStatuses()]);

			usersById.value = userStore.usersById;
			dutiesById.value = dutyStore.dutiesById;

			const driversArray = await loadDrivers();
			drivers.value = Object.fromEntries(driversArray.map((d) => [d.id, d]));
			allDriverIds.value = Object.keys(drivers.value);

			const filteredDriversResult = ui.filters.showInactiveUsers
				? drivers.value
				: Object.fromEntries(Object.entries(drivers.value).filter(([, d]) => d.isActive));

			const fallbackDrivers = Object.keys(drivers.value);
			ui.filters.filteredDrivers = ui.filters.userList?.length > 0 ? ui.filters.userList : fallbackDrivers;

			if (ui.filters.violations) {
				await fetchFilterResult();
			}

			const currentData = JSON.parse(localStorage.getItem(STORAGE_KEYS.PLANNING_TABLE_DATA) || '{}');
			localStorage.setItem(
				STORAGE_KEYS.PLANNING_TABLE_DATA,
				JSON.stringify({
					...currentData,
					filteredDrivers: ui.filters.filteredDrivers,
				})
			);

			return filteredDriversResult;
		} catch (error: unknown) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function fetchFilterResult(): Promise<void> {
		try {
			let from = ui.filters.fromDate ? DateTime.fromISO(ui.filters.fromDate) : undefined;
			let to = ui.filters.toDate ? DateTime.fromISO(ui.filters.toDate) : undefined;
			const violations = ui.filters.violations;
			const userList = ui.filters.filteredDrivers;

			const response = await dutyApi.fetchFilterResult(from, to, violations, userList);
			ui.filters.filteredDrivers = response;
			ui.filters.userList = response;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadManualWorktimeBalances(userId: string, month = ''): Promise<void> {
		loading.manualBalances = true;
		errors.manualBalances = null;
		try {
			const manualBalanceStore = useManualBalanceStore();
			const result = await manualBalanceStore.loadManualWorktimeBalances(userId, month);
			manualBalancesById.value = result.reduce<Record<string, ApiManualBalance>>((acc, balance) => {
				acc[balance.day.id] = balance;
				return acc;
			}, {});
		} catch (error) {
			errors.manualBalances = error instanceof Error ? error.message : 'Could not load manual work time balances.';

			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		} finally {
			loading.manualBalances = false;
		}
	}

	async function evaluateSingleDriver(driverId: string, from: string, to: string, debug = false): Promise<ApiEvaluateSingleDriverResponse | null> {
		loading.assignments = true;
		errors.evaluation = null;
		try {
			const rosterStore = useRosterStore();
			const result = await rosterStore.evaluateSingleDriver(driverId, from, to, debug);
			if (result) {
				evaluationsById.value = { ...evaluationsById.value, [driverId]: result };
			}
			return result;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		} finally {
			loading.assignments = false;
		}
	}

	function getManualBalanceAmount(dayId: string | undefined): number | string {
		if (!dayId) return '';
		return manualBalancesById.value[dayId]?.amount ?? '';
	}

	function clearManualBalances(): void {
		manualBalancesById.value = {};
	}

	async function loadDrivers(): Promise<ApiDriverUser[]> {
		try {
			const userStore = useUserStore();
			return await userStore.loadDrivers();
		} catch (error: unknown) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function createAgreement(payload: AgreementPayload): Promise<DutyChangeLogPayload> {
		try {
			return await agreementApi.createAgreement(payload);
		} catch (error: unknown) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	function setInitial(value: boolean): void {
		loading.initial = value;
	}

	function setIndicatorFilter(indicators: IndicatorOption[]): void {
		ui.filters.selectedIndicators = indicators;
	}

	function setSelectedDay(day: DayUiItem | null): void {
		ui.selectedDate = day;
	}

	function setSelectedDuty(duty: DutyItemResponse | null): void {
		ui.selectedDuty = duty;
	}

	function setSelectedDriver(driver: Driver | null): void {
		isStreamingPaused.value = !!driver;
		ui.selectedDriver = driver;
	}

	function setShowRightPanel(value: boolean): void {
		ui.filters.showRightPanel = value;
	}

	function addDriver(driver: ApiDriverUser): void {
		drivers.value[driver.id] = driver;
		triggerRef(drivers);
	}

	function updateDriver(driver: ApiDriverUser): void {
		if (drivers.value[driver.id]) {
			drivers.value[driver.id] = {
				...drivers.value[driver.id],
				...driver,
			};
			triggerRef(drivers);
		}
	}

	function deleteDriver(driverId: ApiDriverUser['id']): void {
		if (drivers.value[driverId]) {
			delete drivers.value[driverId];
			triggerRef(drivers);
		}
	}

	return {
		drivers,
		allDriverIds,
		usersById,
		dutiesById,
		evaluationsById,
		manualBalancesById,
		isStreamingPaused,

		// Stats Fields
		statistics,
		singleStatistics,
		statisticsByStatus,
		extraPayCurrency,

		...toRefs(ui),
		...toRefs(loading),
		...toRefs(errors),

		activeUsers,
		allUsers,
		inactiveUsers,
		getEvaluationByDriverId,
		manualWorktimeBalances,
		getManualBalanceAmount,
		setStats,
		load,
		loadDrivers,
		addDriver,
		updateDriver,
		deleteDriver,
		initPlanningView,
		evaluateSingleDriver,
		loadManualWorktimeBalances,
		clearManualBalances,
		setInitial,
		setIndicatorFilter,
		setSelectedDriver,
		setShowRightPanel,
		createAgreement,
		canEditDutyForDate,
		fetchFilterResult,
		setSelectedDay,
		setSelectedDuty,

		// Stats Actions
		getStatistics,
		loadSingleStatistics,
		loadStatisticsByDayStatus,
		clearStatistics,
	};
});
